import type { MailboxInfo } from 'owa-client-ids';
import {
    AccountSourceType,
    addAccount,
    getAccountBySourceId,
    M365AuthProvider,
    MailboxType,
} from 'owa-account-source-list-store';
import { HostSourceInformation, SourceAuthType } from './types';
import { globalProfile, microsoft365SourceType, buildSourceId } from './sourceIdBuilder';

/**
 * Returns the MailboxInfo for the specified sourceId
 * @param sourceId the sourceId for the account
 * @returns The mailbox information, or undefined if the account is not a mailbox
 */
export function getMailboxBySourceId(sourceId: string): MailboxInfo | undefined {
    const sourceInfo = getAccountBySourceId(sourceId);
    return sourceInfo
        ? {
              type: sourceInfo!.m365mailbox!.type,
              userIdentity: sourceInfo!.m365mailbox!.userIdentity,
              mailboxSmtpAddress: sourceInfo!.m365mailbox!.emailAddress,
          }
        : undefined;
}

export function getMailboxTypeBySourceId(sourceId: string): string | undefined {
    const sourceInfo = getAccountBySourceId(sourceId);
    switch (sourceInfo?.sourceType) {
        case AccountSourceType.Office365:
            return 'OFFICE365';

        case AccountSourceType.OutlookDotCom:
            return 'OUTLOOKDOTCOM';

        default:
            return undefined;
    }
}

/**
 * Adds the specified host account to the source list store, and returns the sourceId
 * @param account
 */
export function addHostAccount(account: HostSourceInformation): string {
    const sourceId = buildSourceId(
        globalProfile,
        microsoft365SourceType,
        account.mailbox!.userIdentity
    );
    if (!getAccountBySourceId(sourceId)) {
        const characteristics = {
            ...account.characteristics,
            isMicrosoft365Hosted: true,
        };

        const m365mailbox = account.mailbox
            ? {
                  ...account.mailbox,
                  type: MailboxType.UserMailbox,
                  logonEmailAddress: account.mailbox.loginAddress,
                  authProviderType:
                      account.authProviderType === SourceAuthType.Microsoft365
                          ? M365AuthProvider.AAD
                          : M365AuthProvider.MSA,
              }
            : undefined;

        const accountSource = {
            sourceId: sourceId,
            sourceType:
                account.authProviderType === SourceAuthType.Microsoft365
                    ? AccountSourceType.Office365
                    : AccountSourceType.OutlookDotCom,
            displayName: account.displayName,
            characteristics: characteristics,
            m365mailbox: m365mailbox,
        };

        addAccount(accountSource);
    }

    return sourceId;
}
